import { useContext, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
// Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";
// Components
import { DeleteDraftOrderDialog } from "./DeleteDraftOrderDialog";
import { DataTable } from "../../components/DataTable";
// Utils
import { currencyFormatter } from "../../utils/Utils";
// MUI
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import IconButton from "@mui/material/IconButton";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlined";
import ErrorIcon from "@mui/icons-material/Error";
import ReplayIcon from "@mui/icons-material/Replay";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";

const rowStyles = {
  ".processing": { color: "grey" },
  ".awaitingApproval": { color: "primary.light" },
};

const getRowClassName = (row) => {
  if (row.processing === "true") return "processing";
  if (row.awaitingApproval === "Yes") return "awaitingApproval";
  return "";
};

export function OrderTable({
  data,
  loading,
  orderFilter,
  setOrderFilter,
  status,
  columnVisibilityModel,
  canMerge,
  selectedRows,
  setSelectedRows,
}) {
  const navigate = useNavigate();
  const { setAlertNotification } = useContext(AlertNotificationContext);

  // Merging is only offered on draft orders.
  const isDraft = status === "draft";

  // Set the list variable name
  const listName =
    status === "draft" || status === "approved-orders" ? "order" : "invoice";
  const orders = data?.[listName] || [];
  const rowCount = Number(data?.paging?.noOfRecords) || 0;

  // Pagination Model
  const pagination = {
    page: (orderFilter?.page ?? 1) - 1,
    pageSize: orderFilter?.pageSize ?? 25,
  };

  const openTracking = (row) => {
    if (row.deliveryStatus === "Cancelled") return;
    const agent = row.shippingAgent;
    let trackingUrl = "";
    if (agent === "DX") {
      trackingUrl = `https://my.dxdelivery.com/t/${row.consignmentNo}`;
    } else if (agent === "DPD" || agent === "DPD1") {
      const postcode = (row.postCode || "").replace(/\s/g, "");
      trackingUrl = `https://www.dpd.co.uk/content/how-can-we-help/index.jsp?reference=${row.consignmentNo}&postcode=${postcode}`;
    }
    if (trackingUrl) {
      window.open(trackingUrl, "_blank", "noopener,noreferrer");
    } else {
      setAlertNotification({
        message: `No tracking link available for shipping agent: ${agent || "Unknown"}`,
        severity: "warning",
      });
    }
  };

  const copyPostcode = (postCode) => {
    navigator.clipboard.writeText(postCode);
    setAlertNotification({
      message: `${postCode} copied to clipboard`,
      severity: "info",
    });
  };

  const columns = useMemo(
    () => createColumns({ navigate, openTracking, copyPostcode }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate],
  );

  const handlePaginationChange = (next) => {
    setOrderFilter((prevState) => ({
      ...prevState,
      page: next.page + 1,
    }));
  };

  const handleRowClick = (row) => {
    if (row.processing === "true") return;
    let baseURL = status;
    if (status === "completed") baseURL = "invoices";
    if (status === "approved-orders") baseURL = "received";
    if (status === "approved-invoices") baseURL = "invoices";
    navigate({ to: `/${baseURL}/${row.no}` });
  };

  // Drafts are one row per order, so the order no. is a stable id and is what
  // the merge endpoint expects. Other lists have a row per wearer.
  const getRowId = (row) => (isDraft ? row.no : `${row.no}-${row.wearerId}`);

  // Only allow selection of rows that are not processing
  const isRowSelectable = (row) => row.processing !== "true";

  return (
    <Box sx={{ height: "100%" }}>
      <DataTable
        aria-label="Orders"
        columns={columns[status]}
        rows={orders}
        getRowId={getRowId}
        columnVisibility={columnVisibilityModel}
        rowCount={rowCount}
        page={pagination.page}
        pageSize={pagination.pageSize}
        loading={loading}
        onPaginationChange={handlePaginationChange}
        onRowClick={handleRowClick}
        getRowClassName={getRowClassName}
        selectable={canMerge}
        selectedIds={selectedRows}
        onSelectionChange={setSelectedRows}
        isRowSelectable={isRowSelectable}
        sx={rowStyles}
      />
    </Box>
  );
}

const getStatusChipProps = (value) => {
  switch (value) {
    case "Open":
      return {
        icon: <PublishedWithChangesIcon />,
        label: "In Progress",
        color: "warning",
        variant: "outlined",
      };
    case "Released":
      return {
        icon: <LocalShippingRoundedIcon />,
        label: "Part Shipped",
        color: "secondary",
        variant: "outlined",
      };
  }
};

// const currencyFormatter = (currencyCode, value) => {
//   switch (currencyCode) {
//     case "EUR":
//       return `€${value}`;
//     case "USD":
//       return `$${value}`;
//     default:
//       return `£${value}`;
//   }
// };

export const getShippingChipProps = (value) => {
  switch (value) {
    case "New":
      return {
        icon: <AutoAwesomeOutlinedIcon />,
        label: "New",
        color: "warning",
        variant: "outlined",
      };
    case "In transit":
      return {
        icon: <LocalShippingRoundedIcon />,
        label: "In transit",
        color: "secondary",
        variant: "outlined",
      };
    case "Delivered":
      return {
        icon: <TaskAltIcon />,
        label: "Complete",
        color: "success",
        variant: "outlined",
      };
    case "Exception":
      return {
        icon: <ErrorIcon />,
        label: "Issue",
        color: "error",
        variant: "outlined",
      };
    case "Returned":
      return {
        icon: <ReplayIcon />,
        label: "Returned",
        color: "primary",
        variant: "outlined",
      };
    default:
      return {
        icon: <HelpOutlineIcon />,
        label: value,
        variant: "outlined",
      };
  }
};

const money = (field, header) => ({
  id: field,
  accessorFn: (row) => currencyFormatter(row.currencyCode, row[field]),
  header,
  meta: { minWidth: 100 },
});

const createColumns = ({ navigate, openTracking, copyPostcode }) => ({
  draft: [
    { accessorKey: "no", header: "No.", meta: { minWidth: 100 } },
    {
      accessorKey: "status",
      header: "Status",
      meta: { minWidth: 150 },
      cell: (info) => {
        if (info.getValue() === "") return null;
        if (info.row.original.awaitingApproval === "Yes")
          return "Awaiting Approval";
        if (info.row.original.processing === "true") return "Processing..";
        return null;
      },
    },
    { accessorKey: "createdBy", header: "Created By", meta: { minWidth: 200 } },
    { accessorKey: "orderDate", header: "Date", meta: { minWidth: 100 } },
    { accessorKey: "postcode", header: "Postcode", meta: { minWidth: 100 } },
    {
      id: "orderTotal",
      accessorFn: (row) => currencyFormatter("GB", row.orderTotal),
      header: "Net Total",
      meta: { minWidth: 100 },
    },
    {
      id: "delete",
      header: "Delete",
      meta: { align: "center" },
      cell: (info) => (
        // The dialog sits inside a clickable row, so swallow the click here.
        <Box onClick={(event) => event.stopPropagation()}>
          <DeleteDraftOrderDialog
            orderNo={info.row.original.no}
            disabled={info.row.original.processing !== "false"}
          />
        </Box>
      ),
    },
  ],
  received: [
    { accessorKey: "no", header: "No.", meta: { minWidth: 100 } },
    { accessorKey: "orderDate", header: "Date", meta: { minWidth: 100 } },
    { accessorKey: "createdName", header: "Creator", meta: { minWidth: 100 } },
    {
      accessorKey: "orderedName",
      header: "Authoriser",
      meta: { minWidth: 100 },
    },
    { accessorKey: "reference", header: "Reference", meta: { minWidth: 100 } },
    { accessorKey: "wearerName", header: "Wearer", meta: { minWidth: 100 } },
    { accessorKey: "postCode", header: "Postcode", meta: { minWidth: 100 } },
    money("amount", "Amount"),
    {
      accessorKey: "status",
      header: "Status",
      meta: { minWidth: 150 },
      cell: (info) =>
        info.getValue() === "" ? null : (
          <Chip variant="outlined" {...getStatusChipProps(info.getValue())} />
        ),
    },
  ],
  "approved-orders": [
    { accessorKey: "no", header: "No.", meta: { minWidth: 100 } },
    { accessorKey: "orderDate", header: "Date", meta: { minWidth: 100 } },
    {
      accessorKey: "createdName",
      header: "Requested By",
      meta: { minWidth: 100 },
    },
    {
      accessorKey: "orderedName",
      header: "Approved By",
      meta: { minWidth: 100 },
    },
    { accessorKey: "reference", header: "Reference", meta: { minWidth: 100 } },
    { accessorKey: "postCode", header: "Postcode", meta: { minWidth: 100 } },
    money("amount", "Amount"),
    {
      accessorKey: "status",
      header: "Status",
      meta: { minWidth: 150 },
      cell: (info) =>
        info.getValue() === "" ? null : (
          <Chip variant="outlined" {...getStatusChipProps(info.getValue())} />
        ),
    },
  ],
  completed: [
    {
      accessorKey: "salesOrderNo",
      header: "Order No.",
      meta: { minWidth: 100 },
    },
    { accessorKey: "no", header: "Invoice No.", meta: { minWidth: 100 } },
    {
      accessorKey: "shipmentNo",
      header: "Shipment No.",
      meta: { minWidth: 100 },
    },
    {
      accessorKey: "shipmentDate",
      header: "Despatch Date",
      meta: { minWidth: 100 },
    },
    {
      accessorKey: "deliveryStatus",
      header: "Delivery Status",
      meta: { minWidth: 150 },
      cell: (info) => {
        const value = info.getValue();
        if (value === "") return null;
        return (
          <Tooltip title="Open Tracking">
            <Chip
              variant="outlined"
              {...getShippingChipProps(value)}
              clickable={value !== "Cancelled"}
              onClick={(event) => {
                // Tracking replaces the row's own navigation.
                event.stopPropagation();
                openTracking(info.row.original);
              }}
            />
          </Tooltip>
        );
      },
    },
    { accessorKey: "createdName", header: "Creator", meta: { minWidth: 100 } },
    {
      accessorKey: "orderedName",
      header: "Authoriser",
      meta: { minWidth: 100 },
    },
    { accessorKey: "reference", header: "Reference", meta: { minWidth: 100 } },
    { accessorKey: "wearerName", header: "Wearer", meta: { minWidth: 100 } },
    {
      accessorKey: "postCode",
      header: "Postcode",
      meta: { minWidth: 150 },
      cell: (info) =>
        info.getValue() && (
          <Tooltip title="Copy Postcode">
            <Stack
              direction="row"
              spacing={1}
              sx={{
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={(event) => {
                // Copying replaces the row's own navigation.
                event.stopPropagation();
                copyPostcode(info.getValue());
              }}
            >
              <ContentCopyIcon fontSize="small" />
              <Typography>{info.getValue()}</Typography>
            </Stack>
          </Tooltip>
        ),
    },
    money("amount", "Amount"),
    {
      accessorKey: "allowReturn",
      header: "Return",
      meta: { align: "center" },
      cell: (info) => (
        <IconButton
          aria-label="return"
          color="warning"
          disabled={info.getValue() !== "True"}
          onClick={(event) => {
            event.stopPropagation();
            navigate({
              to: "/returns/new",
              search: { invoice: info.row.original.no },
            });
          }}
        >
          <UndoOutlinedIcon />
        </IconButton>
      ),
    },
  ],
  "approved-invoices": [
    { accessorKey: "no", header: "No.", meta: { minWidth: 100 } },
    {
      accessorKey: "shipmentNo",
      header: "Shipment No.",
      meta: { minWidth: 100 },
    },
    {
      accessorKey: "shipmentDate",
      header: "Despatch Date",
      meta: { minWidth: 100 },
    },
    {
      accessorKey: "deliveryStatus",
      header: "Delivery Status",
      meta: { minWidth: 150 },
      cell: (info) => {
        const value = info.getValue();
        if (value === "") return null;
        return (
          <Chip
            variant="outlined"
            {...getShippingChipProps(value)}
            clickable={value !== "Cancelled"}
            onClick={(event) => {
              event.stopPropagation();
              openTracking(info.row.original);
            }}
          />
        );
      },
    },
    {
      accessorKey: "createdName",
      header: "Requested By",
      meta: { minWidth: 100 },
    },
    {
      accessorKey: "orderedName",
      header: "Authoriser",
      meta: { minWidth: 100 },
    },
    { accessorKey: "reference", header: "Reference", meta: { minWidth: 100 } },
    { accessorKey: "postCode", header: "Postcode", meta: { minWidth: 100 } },
    money("amount", "Amount"),
    {
      accessorKey: "allowReturn",
      header: "Return",
      meta: { align: "center" },
      cell: (info) => (
        <IconButton
          aria-label="return"
          color="warning"
          disabled={info.getValue() !== "True"}
          onClick={(event) => {
            event.stopPropagation();
            navigate({
              to: "/returns/new",
              search: { invoice: info.row.original.no },
            });
          }}
        >
          <UndoOutlinedIcon />
        </IconButton>
      ),
    },
  ],
});
